import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, File, X, CheckCircle, AlertCircle } from 'lucide-react';
import type { RequiredDocument, ProjectDocument } from '../../types/wizard';

interface RequiredDocumentsUploaderProps {
  requiredDocuments: RequiredDocument[];
  uploadedDocuments: ProjectDocument[];
  onUpload: (type: string, file: File) => Promise<void>;
  onDelete: (documentId: string) => Promise<void>;
  isLoading?: boolean;
}

export const RequiredDocumentsUploader: React.FC<RequiredDocumentsUploaderProps> = ({
  requiredDocuments,
  uploadedDocuments,
  onUpload,
  onDelete,
  isLoading = false
}) => {
  return (
    <div className="p-8">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Documents requis
          </h2>
          <p className="text-gray-600">
            Veuillez téléverser les documents suivants pour finaliser votre dossier
          </p>
        </div>

        <div className="space-y-6">
          {requiredDocuments.map((docType) => {
            const uploadedDoc = uploadedDocuments.find(doc => {
              // Check against document_subtype (new system) or fallback to type (old system)
              const docSubtype = doc.document_subtype || doc.type;
              return docSubtype === docType.type.toUpperCase();
            });

            return (
              <DocUploadItem
                key={docType.type}
                docType={docType}
                uploadedDocument={uploadedDoc}
                onUpload={onUpload}
                onDelete={onDelete}
                isLoading={isLoading}
              />
            );
          })}
        </div>

        {/* Summary */}
        <div className="mt-8 p-6 bg-gray-50 rounded-xl">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-gray-900">Progression des documents</h3>
              <p className="text-sm text-gray-600">
                {uploadedDocuments.length} sur {requiredDocuments.length} documents téléversés
              </p>
            </div>
            <div className="flex items-center space-x-2">
              {uploadedDocuments.length === requiredDocuments.length ? (
                <>
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-green-600 font-medium">Complet</span>
                </>
              ) : (
                <>
                  <AlertCircle className="w-5 h-5 text-orange-600" />
                  <span className="text-orange-600 font-medium">
                    {requiredDocuments.length - uploadedDocuments.length} document(s) manquant(s)
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

interface DocUploadItemProps {
  docType: RequiredDocument;
  uploadedDocument?: ProjectDocument;
  onUpload: (type: string, file: File) => Promise<void>;
  onDelete: (documentId: string) => Promise<void>;
  isLoading: boolean;
}

const DocUploadItem: React.FC<DocUploadItemProps> = ({
  docType,
  uploadedDocument,
  onUpload,
  onDelete,
  isLoading
}) => {
  const [uploadError, setUploadError] = React.useState<string | null>(null);
  const [isUploading, setIsUploading] = React.useState(false);

  const validateFile = (file: File): string | null => {
    const fileExt = file.name.split('.').pop()?.toLowerCase();
    if (!fileExt || !docType.extensions.includes(fileExt)) {
      return `Format non supporté. Formats acceptés: ${docType.extensions.join(', ')}`;
    }
    
    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > docType.maxMB) {
      return `Fichier trop volumineux. Taille maximum: ${docType.maxMB}MB`;
    }
    
    return null;
  };

  const handleUpload = async (file: File) => {
    setUploadError(null);
    const error = validateFile(file);
    if (error) {
      setUploadError(error);
      return;
    }

    setIsUploading(true);
    try {
      await onUpload(docType.type, file);
    } catch (err: any) {
      setUploadError(err.message || 'Erreur lors du téléversement');
    } finally {
      setIsUploading(false);
    }
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      handleUpload(acceptedFiles[0]);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'text/markdown': ['.md'],
      'text/plain': ['.txt'],
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg']
    },
    maxFiles: 1,
    disabled: isLoading || isUploading || !!uploadedDocument
  });

  const handleDelete = async () => {
    if (uploadedDocument) {
      try {
        await onDelete(uploadedDocument.id);
      } catch (err: any) {
        setUploadError(err.message || 'Erreur lors de la suppression');
      }
    }
  };

  return (
    <div className="border border-gray-200 rounded-xl p-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="font-semibold text-gray-900">{docType.label}</h3>
          <p className="text-sm text-gray-600 mt-1">
            Formats acceptés: {docType.extensions.join(', ')} • Max {docType.maxMB}MB
          </p>
        </div>
        {uploadedDocument && (
          <div className="flex items-center space-x-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <span className="text-sm text-green-600 font-medium">Téléversé</span>
          </div>
        )}
      </div>

      {uploadedDocument ? (
        <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center space-x-3">
            <File className="w-5 h-5 text-green-600" />
            <div>
              <p className="font-medium text-green-900">{uploadedDocument.filename}</p>
              <p className="text-sm text-green-700">
                {(uploadedDocument.file_size / (1024 * 1024)).toFixed(2)} MB
              </p>
            </div>
          </div>
          <button
            onClick={handleDelete}
            disabled={isLoading}
            className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
            aria-label="Supprimer le document"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <div
          {...getRootProps()}
          className={`
            border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
            ${isDragActive 
              ? 'border-blue-400 bg-blue-50' 
              : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
            }
            ${(isLoading || isUploading) ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        >
          <input {...getInputProps()} />
          
          {isUploading ? (
            <div className="flex flex-col items-center space-y-2">
              <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
              <p className="text-blue-600 font-medium">Téléversement en cours...</p>
            </div>
          ) : (
            <div className="flex flex-col items-center space-y-2">
              <Upload className="w-8 h-8 text-gray-400" />
              <div>
                <p className="text-gray-600">
                  {isDragActive 
                    ? 'Déposez le fichier ici...' 
                    : 'Glissez-déposez un fichier ou cliquez pour sélectionner'
                  }
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  {docType.extensions.join(', ')} • Max {docType.maxMB}MB
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {uploadError && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center space-x-2 text-red-600">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span className="text-sm">{uploadError}</span>
          </div>
        </div>
      )}
    </div>
  );
};